import { FieldValues } from 'react-hook-form'
import axiosInstance from '../utils/axiosInstance'
import { API_END_POINT } from '../utils/environment'
import {
  CreateChildInstanceType,
  CreateNewCaseType,
  PatientInfoPayloadType,
  PaySignInvokePayloadType,
  TaskPayload,
  WorkflowItem,
} from '../types'
import { FILTER_KEYS } from '../constants'
import {
  buildProgramServiceHeaders,
  validateApiParams,
} from '../utils/functions'

export const getAllWorkflowList = async (serviceIds?: Set<number>) => {
  const url = `${API_END_POINT?.WORKFLOW}/process/definitions`
  const response = await axiosInstance.get(url)
  // First filter out items with "Testing" in their name, then remove name property from remaining items
  // TODO: Remove this after UAT testing
  if (response?.data) {
    response.data = response.data.filter((item: WorkflowItem) => {
      if (
        serviceIds &&
        !serviceIds.has(item?.global_variables?.inherited_variables?.service_id)
      ) {
        return false
      }
      return !item?.name
        ?.toLowerCase()
        .includes(FILTER_KEYS.DO_NOT_USE?.toLowerCase())
    })
  }
  return response
}

export const createWorkflowInstance = async (payload: {
  data: FieldValues
  InstanceId: string | null
}) => {
  const url = `${API_END_POINT?.WORKFLOW}/process/${payload?.InstanceId}/instance`
  validateApiParams(
    { InstanceId: payload.InstanceId, ...payload.data },
    {
      endpoint: url,
      method: 'POST',
    }
  )
  return await axiosInstance.post(url, payload?.data)
}

export const completeTaskByTaskIdApi = async (payload: {
  taskId: string
  meta_data: { entityId?: string; layoutId?: string }
  forced?: boolean
  changeStatus?: string
}) => {
  const {
    taskId,
    meta_data,
    forced = false,
    changeStatus = 'COMPLETED',
  } = payload

  const url = `${API_END_POINT?.V2_WORKFLOW}/process/task/${taskId}?state=${changeStatus}&forced=${forced}`
  validateApiParams(
    {
      taskId: taskId,
      changeStatus: changeStatus,
      forced: forced,
      ...meta_data,
    },
    {
      endpoint: url,
      method: 'PATCH',
    }
  )

  return await axiosInstance.patch(url, meta_data)
}

export const cancelTaskByTaskIdApi = async (payload: {
  taskId: string
  meta_data: { entityId?: string; layoutId?: string }
  forced?: boolean
}) => {
  const url = `${API_END_POINT?.WORKFLOW}/process/task/${
    payload.taskId
  }?state=CANCELLED&forced=${!!payload?.forced}`
  validateApiParams(
    {
      taskId: payload.taskId,
      forced: payload.forced,
      ...payload.meta_data,
    },
    {
      endpoint: url,
      method: 'PATCH',
    }
  )
  return await axiosInstance.patch(url, payload.meta_data)
}

export const getWorkflowByEntityNameAndRecordId = async (payload: {
  recordId: string
  entityName: string
}) => {
  const url = `${API_END_POINT?.WORKFLOW}/process/${payload?.entityName}/${payload.recordId}/instance`
  validateApiParams(payload, {
    endpoint: url,
    method: 'GET',
  })
  return await axiosInstance.get(url)
}

export const getTaskListApi = async (payload: unknown) => {
  const url = `${API_END_POINT?.WORKFLOW}/process/get/tasks?size=100`
  return await axiosInstance.post(url, payload)
}

export const createTaskApi = async (payload: {
  process_instance_id: string | null
  case_id: string
  name: string
  description: string
  due_date?: string
  start_date?: string
  completion_date?: string
  outcome?: string
  step_id?: string
  state: string
  standalone: boolean
  cancellable?: boolean
  record_status: string
  notes?: string
  identifier?: {
    ui_identifier?: string
    document_id?: string
    source_intake_id?: string
    disable_get_member_id?: boolean
    disabled_fields?: string[]
    taskNoteValues?: {
      isTaskNoteAddButton?: boolean
      isTaskNoteSuggestionBox?: boolean
    }
  }
  task_template?: {
    id?: number
    name?: string
  }
  entity_type_name?: string
  entity_name?: string
  assignee?: {
    id?: number
  }
  requestor_view?: {
    id?: number | string | null
  }
}) => {
  const url = `${API_END_POINT?.WORKFLOW}/process/${payload.process_instance_id}/task`
  // RUM 005: block empty process/case ids before they become HTTP 400
  validateApiParams(
    {
      process_instance_id: payload.process_instance_id,
      case_id: payload.case_id,
    },
    {
      endpoint: url,
      method: 'POST',
    }
  )
  return await axiosInstance.post(url, payload)
}

export const getCaseDedupeStageData = async (caseId: number) => {
  const url = `${API_END_POINT.CASE_AUTOMATION}v1/staging/case/${caseId}/status`
  return await axiosInstance.get(url)
}

export const getStagingDedupeStatusData = async (
  stagingRecordId: number | string
) => {
  const url = `${API_END_POINT.CASE_AUTOMATION}v1/staging/${stagingRecordId}/status`
  return await axiosInstance.get(url)
}

export const previewAndDownload = async (payload: {
  entityId: number
  id: number
}) => {
  const url = `${API_END_POINT?.TEMPLATE}/v1/communications/doc-data/${payload.entityId}/${payload.id}`
  return await axiosInstance.get(url)
}

export const taskUpdateApi = async (
  payload: {
    taskId: string
    data: FieldValues
  },
  programId?: string,
  serviceId?: string
) => {
  const url = `${API_END_POINT?.WORKFLOW}/tasks/${payload?.taskId}`
  validateApiParams(
    { taskId: payload?.taskId },
    {
      endpoint: url,
      method: 'PATCH',
    }
  )
  return await axiosInstance.patch(url, payload?.data, {
    headers: buildProgramServiceHeaders(programId, serviceId),
  })
}

export const interruptWorkFlow = async (payload: {
  workflowId: string
  signal: string
}) => {
  const url = `${API_END_POINT?.WORKFLOW}/process/${payload?.workflowId}/interrupt?signal=${payload?.signal}`
  validateApiParams(payload, {
    endpoint: url,
    method: 'POST',
  })
  return await axiosInstance.post(url)
}

export const getAllWorkflowListUsingServiceId = async (
  definitionId: number,
  programId: number
) => {
  const url = `${API_END_POINT?.WORKFLOW}/process/definition/${definitionId}?programId=${programId}`
  const response = await axiosInstance.get(url)
  return response
}

export const fetchCaseIdUsingWorkFlowAndServiceId = async (
  payload: CreateNewCaseType
) => {
  const url = `${API_END_POINT?.WORKFLOW}/process/childInstance?definitionId=${payload?.definitionId}&comunicationId=${payload?.comunicationId}&reviewer=${payload?.reviewer}&serviceId=${payload?.serviceId}`
  const response = await axiosInstance.post(url, {
    case_id: payload?.case_id,
    show_intake_form: true,
    source_id: payload?.source_id,
  })
  return response
}

export const fetchCaseIdUsingWorkFlowAndDefinitionId = async (
  payload: CreateChildInstanceType,
  targetDefinitionId: string | number,
  sourceDefinitionId: string | number,
  reviewer: string,
  communicationId?: string | number
) => {
  return await axiosInstance.post(
    `${API_END_POINT?.WORKFLOW}/process/childInstance?${
      communicationId ? `comunicationId=${communicationId}&` : ''
    }reviewer=${reviewer}&targetDefinitionId=${targetDefinitionId}&sourceDefinitionId=${sourceDefinitionId}`,
    payload
  )
}
export const fetchAssigneeApi = async (
  service_id: number,
  program_id: number
) => {
  const url = `${API_END_POINT?.CORE}users/service/${service_id}?programId=${program_id}`
  validateApiParams(
    { service_id, program_id },
    { endpoint: url, method: 'GET' }
  )
  return await axiosInstance.get(url)
}
export const fetchAssigneeApiForPermissions = async (
  permission_id: number,
  program_id: number
) => {
  const url = `${API_END_POINT?.CORE}users/service/${permission_id}?isPermission=true&programId=${program_id}`
  validateApiParams(
    { permission_id, program_id },
    { endpoint: url, method: 'GET' }
  )
  return await axiosInstance.get(url)
}

export const searchPersonEntityApi = async (searchText: string) => {
  const url = `${API_END_POINT?.WORKFLOW}/tasks/search/person/${searchText}`
  return await axiosInstance.get(url)
}

export const searchAccountEntityApi = async (searchText: string) => {
  const url = `${API_END_POINT?.WORKFLOW}/tasks/search/account/${searchText}`
  return await axiosInstance.get(url)
}

export const fetchEntityTypeApi = async () => {
  const url = `${API_END_POINT?.ENTITY_MANAGER}base/entities/searchable`
  return await axiosInstance.get(url)
}

export const fetchEntityApi = async (entityType: string) => {
  const url = `${API_END_POINT?.ENTITY_MANAGER}entities/searchable/${entityType}`
  return await axiosInstance.get(url)
}

export const saveTaskDataApi = async (
  taskId: string,
  payload: TaskPayload,
  programId?: string,
  serviceId?: string
) => {
  const url = `${API_END_POINT?.WORKFLOW}/tasks/${taskId}`
  return await axiosInstance.patch(url, payload, {
    headers: buildProgramServiceHeaders(programId, serviceId),
  })
}

export const paySignCopayEnrollment = async (
  payload: PatientInfoPayloadType,
  product_name: string
) => {
  const url = `${API_END_POINT?.CORE}v1/integrations/${product_name}-PaySign-submit-patient-form`
  return await axiosInstance.post(url, payload)
}

export const paySignSendEnrollmentFaxInvoke = async (
  payload: PaySignInvokePayloadType,
  product_name: string
) => {
  const url = `${API_END_POINT?.CORE}v1/integrations/${product_name}-PaySign-send-enrollment-fax`
  return await axiosInstance.post(url, payload)
}

export const checkPaySignFlowAPI = async (product_name: string) => {
  const url = `${API_END_POINT?.CORE}v1/integrations/${product_name}-PaySign-send-enrollment-fax/metadata`
  return await axiosInstance.get(url)
}

export const getTaskList = async () => {
  const url = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/tasks?entityName=t_task&embedded=id%2Cname`
  const response = await axiosInstance.get(url)
  return response
}
export const postEntityMacros = async (recordId: number, macros: string[]) => {
  const url = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/macros?entityName=t_task&embedded=id%2Cname`
  const payload = { recordId, macros }
  const response = await axiosInstance.post(url, payload)
  return response
}
export const taskUpdateIdentifierApi = async (payload: {
  taskId: string
  data: FieldValues
}) => {
  const url = `${API_END_POINT?.WORKFLOW}/tasks/v2/${payload?.taskId}`
  validateApiParams(
    { taskId: payload?.taskId },
    {
      endpoint: url,
      method: 'PATCH',
    }
  )
  return await axiosInstance.patch(url, payload?.data)
}
export const fetchTaskEntititesDropdownOptionsApi = async (payload: {
  entityType: string
  searchValue?: string
  id?: number
  page: number
  size: number
}) => {
  const baseUrl = `${API_END_POINT?.WORKFLOW}/tasks/search/${payload?.entityType}`

  const params = new URLSearchParams()
  if (payload?.searchValue) params.append('search', payload?.searchValue)
  if (payload?.id) params.append('id', payload?.id?.toString())
  params.append('page', String(payload?.page))
  params.append('size', String(payload?.size))

  const url = `${baseUrl}?${params.toString()}`
  return await axiosInstance.get(url)
}

export const calculateFPL = async (recordId: string) => {
  const url = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/custom/procedure`
  const data = {
    procedureName: 'calculate_income_details',
    params: {
      case_id: {
        value: recordId,
        order: 1,
        is_input_param: true,
        type: 'LONGDATATYPE',
      },
      is_discrepancy: {
        order: 2,
        is_input_param: false,
        type: 'BOOLEANDATATYPE',
      },
    },
  }
  return await axiosInstance.post(url, data)
}

export const reloadEligibilityAssessment = async (
  recordId: string,
  isCpap: boolean,
  schemaName: string
) => {
  const url = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/procedure/${schemaName}`
  const data = {
    params: {
      c_case_id: {
        type: 'LONGDATATYPE',
        order: 1,
        value: recordId,
        is_input_param: true,
      },
      c_eligibility_assessment_id: {
        type: 'LONGDATATYPE',
        order: 2,
        is_input_param: false,
      },
    },
    procedureName: isCpap
      ? 'cpap_check_eligibility_criteria'
      : 'check_order_eligibility_criteria',
    primaryKeyName: 'c_eligibility_assessment_id',
  }

  return await axiosInstance.post(url, data)
}

export const procedureForCreateOrder = async (
  recordId: string,
  value: string
) => {
  const url = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/custom/procedure`
  const data = {
    procedureName: 'copy_shipping_address',
    params: {
      case_id: {
        value: recordId,
        order: 1,
        is_input_param: true,
        type: 'LONGDATATYPE',
      },
      entity_type: {
        order: 2,
        value: value,
        is_input_param: true,
        type: 'STRINGDATATYPE',
      },
    },
  }
  return await axiosInstance.post(url, data)
}
export const changeTaskStatusToWithdraw = async (
  instanceId: string,
  programId?: string,
  serviceId?: string
) => {
  const url = `${API_END_POINT?.WORKFLOW}/process/${instanceId}/withdraw`
  validateApiParams(
    { instanceId },
    {
      endpoint: url,
      method: 'POST',
    }
  )
  return await axiosInstance.post(
    url,
    {},
    {
      headers: buildProgramServiceHeaders(programId, serviceId),
    }
  )
}

export const fetchCaptureApiResponse = async (taskId: string | undefined) => {
  const url = `${API_END_POINT?.INTEGRATIONS}tasks/integrations/${taskId}`
  validateApiParams(
    { taskId },
    {
      endpoint: url,
      method: 'GET',
    }
  )
  return await axiosInstance.get(url)
}

export const fetchCaptureApiDetailsResponse = async (
  requestId: string | undefined
) => {
  const url = `${API_END_POINT?.INTEGRATIONS}tasks/integrationExchanges/${requestId}`
  validateApiParams(
    { requestId },
    {
      endpoint: url,
      method: 'GET',
    }
  )
  return await axiosInstance.get(url)
}
export const fetchEPRQuery = async (
  case_id: number | string,
  patient_id: number | string
) => {
  return await axiosInstance.post(`${API_END_POINT.CORE}v1/epr/query`, {
    caseId: case_id,
    patientId: patient_id,
  })
}

export const addEPRPatient = async (case_id: number, patient_id: number) => {
  return await axiosInstance.post(`${API_END_POINT.CORE}v1/epr/add`, {
    caseId: case_id,
    patientId: patient_id,
  })
}
export const selectEPRPatient = async (case_id: number, patient_id: number) => {
  return await axiosInstance.post(`${API_END_POINT.CORE}v1/epr/select`, {
    caseId: case_id,
    patientId: patient_id,
  })
}

export const getExceptionReasonsOptions = async () => {
  return await axiosInstance.get(
    `${API_END_POINT.ENTITY_MANAGER}entities/name/pap_eligibility_assessments`
  )
}

export const updateIcdCodes = async (
  case_id: string,
  resource_id: string,
  icdCodes: string
) => {
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/custom/procedure`,
    {
      procedureName: 'update_exception_diagnosis_codes',
      params: {
        c_case_id: {
          value: case_id,
          order: 1,
          is_input_param: true,
          type: 'LONGDATATYPE',
        },
        c_eligibility_assessment_id: {
          value: resource_id,
          order: 2,
          is_input_param: true,
          type: 'LONGDATATYPE',
        },
        diagnosis_codes: {
          value: icdCodes,
          order: 3,
          is_input_param: true,
          type: 'STRINGDATATYPE',
        },
      },
    }
  )
}

export const workflowCaseMappingForWebEnrollment = async () => {
  const url = `${API_END_POINT.WORKFLOW}/process/variables/ef9e1aad-cc34-4ccf-bb37-178850b22810`
  return await axiosInstance.get(url)
}

export const intakeCdsRequest = async (payload: {
  document_id: string
  case_id: string
  task_id: string
  mode: string
}) => {
  const url = `${API_END_POINT.V1_CORE}integrations/cds/document`
  return await axiosInstance.post(url, payload)
}

export const resetWorkflowApi = async (caseId: string) => {
  const url = `${API_END_POINT.WORKFLOW}/process/reset/${caseId}`
  return await axiosInstance.post(url)
}

export const calculateFPLPercentage = async (
  p_household_size: string,
  p_annual_income: string,
  p_patient_id: string
) => {
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/custom/function`,
    {
      functionName: 'fpl_percentage_with_patient',
      primaryKeyName: 'result',
      params: {
        p_household_size: {
          value: p_household_size,
          order: 1,
          is_input_param: true,
          type: 'LONGDATATYPE',
        },
        p_annual_income: {
          value: p_annual_income,
          order: 2,
          is_input_param: true,
          type: 'LONGDATATYPE',
        },
        p_patient_id: {
          value: p_patient_id,
          order: 3,
          is_input_param: true,
          type: 'LONGDATATYPE',
        },
      },
    }
  )
}
